import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { AlertTriangle, CalendarClock, CircleDot, Infinity as InfinityIcon } from 'lucide-react';
import { useApi } from '../contexts/ApiContext';
import type { ApiResponse } from '../types';
import { Skeleton } from './ui/Skeleton';
import EmptyState from './ui/EmptyState';
import AckForm, { type AckRef } from './AckForm';

interface Item {
  key: string;
  kind: string;
  severity: 'urgent' | 'warn';
  type: 'deadline' | 'since' | 'ongoing';
  date: string | null;
  title: string;
  detail: string | null;
  link: string;
  ack?: AckRef;
}
interface Data { today: string; items: Item[]; labels: Record<string, string>; truncated: boolean; acknowledged?: number }

const DAY = 86400000;
const toMs = (d: string) => Date.parse(`${d}T00:00:00Z`);
const fmtDay = (d: string) => new Date(toMs(d)).toLocaleDateString(undefined, { weekday: 'short', day: 'numeric', month: 'short', timeZone: 'UTC' });

function relative(d: string, today: string): string {
  const n = Math.round((toMs(d) - toMs(today)) / DAY);
  if (n === 0) return 'today';
  if (n === 1) return 'tomorrow';
  if (n === -1) return 'yesterday';
  return n > 0 ? `in ${n} days` : `${-n} days ago`;
}

/** Deadline buckets, in order. */
const BUCKETS: { key: string; label: string; test: (days: number) => boolean; tone: string }[] = [
  { key: 'overdue', label: 'Overdue', test: (n) => n < 0, tone: 'text-red-700' },
  { key: 'today', label: 'Today', test: (n) => n === 0, tone: 'text-red-700' },
  { key: 'week', label: 'Next 7 days', test: (n) => n >= 1 && n <= 7, tone: 'text-amber-700' },
  { key: 'month', label: 'Next 30 days', test: (n) => n >= 8 && n <= 30, tone: 'text-slate-700' },
  { key: 'later', label: 'Later', test: (n) => n > 30, tone: 'text-slate-500' },
];

function Row({ it, today, onChanged }: { it: Item; today: string; onChanged?: () => void }) {
  const [acking, setAcking] = useState(false);
  return (
    <li>
      <div className="group -mx-1.5 flex items-start gap-2 rounded px-1.5 py-1 hover:bg-slate-50">
        <span className={`mt-1.5 h-2 w-2 shrink-0 rounded-full ${it.severity === 'urgent' ? 'bg-red-500' : 'bg-amber-400'}`} />
        <Link to={it.link} className="min-w-0 flex-1">
          <span className="block truncate text-sm text-brand-600 group-hover:underline">{it.title}</span>
          {it.detail && <span className="block truncate text-xs text-slate-500" title={it.detail}>{it.detail}</span>}
        </Link>
        {it.date && <span className="shrink-0 pt-0.5 text-xs tabular-nums text-slate-400">{relative(it.date, today)}</span>}
        {it.ack && !acking && (
          <button type="button" onClick={() => setAcking(true)} title="Acknowledge with a comment"
            className="shrink-0 rounded border border-slate-200 px-1.5 py-0.5 text-[11px] font-medium text-slate-500 hover:border-slate-300 hover:text-slate-800">Ack</button>
        )}
      </div>
      {it.ack && acking && <AckForm ack={it.ack} onCancel={() => setAcking(false)} onDone={() => { setAcking(false); onChanged?.(); }} />}
    </li>
  );
}

/** Items grouped under date headings (one heading per day). */
function ByDay({ items, today, onChanged }: { items: Item[]; today: string; onChanged?: () => void }) {
  const days: [string, Item[]][] = [];
  for (const it of items) {
    const last = days[days.length - 1];
    if (last && last[0] === it.date) last[1].push(it);
    else days.push([it.date!, [it]]);
  }
  return (
    <ol className="relative ml-1 space-y-3 border-l border-slate-200 pl-4">
      {days.map(([d, list]) => (
        <li key={d} className="relative">
          <span className="absolute -left-[21px] top-1.5 h-2.5 w-2.5 rounded-full border-2 border-surface bg-slate-300" />
          <div className="mb-0.5 text-xs font-medium text-slate-600">{fmtDay(d)} <span className="font-normal text-slate-400">· {relative(d, today)}</span></div>
          <ul>{list.map((it) => <Row key={it.key} it={it} today={today} onChanged={onChanged} />)}</ul>
        </li>
      ))}
    </ol>
  );
}

/**
 * The dashboard's "Needs attention" items on a timeline: what's coming due
 * (by deadline), what's broken (since when), and what's ongoing (no date).
 */
export default function AttentionTimeline({ onChanged }: { onChanged?: () => void } = {}) {
  const { fetchApi } = useApi();
  const [data, setData] = useState<Data | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [hidden, setHidden] = useState<Set<string>>(() => {
    try { return new Set(JSON.parse(localStorage.getItem('attentionTimelineHidden') || '[]')); } catch { return new Set(); }
  });
  const [urgentOnly, setUrgentOnly] = useState(false);

  const [version, setVersion] = useState(0);
  // After an ack: refetch here, and let the dashboard refresh its counts.
  const reload = () => { setVersion((v) => v + 1); onChanged?.(); };
  useEffect(() => {
    fetchApi<ApiResponse<Data>>('/reports/attention-timeline')
      .then((r) => setData(r.data ?? null))
      .catch((e) => setError(e instanceof Error ? e.message : 'Could not load the timeline'));
  }, [fetchApi, version]);

  const toggleKind = (k: string) => setHidden((h) => {
    const n = new Set(h);
    if (n.has(k)) n.delete(k); else n.add(k);
    try { localStorage.setItem('attentionTimelineHidden', JSON.stringify([...n])); } catch { /* per-viewer convenience only */ }
    return n;
  });

  const view = useMemo(() => {
    if (!data) return null;
    const counts = new Map<string, number>();
    for (const it of data.items) counts.set(it.kind, (counts.get(it.kind) ?? 0) + 1);
    const shown = data.items.filter((it) => !hidden.has(it.kind) && (!urgentOnly || it.severity === 'urgent'));
    const deadlines = shown.filter((it) => it.type === 'deadline' && it.date).sort((a, b) => a.date!.localeCompare(b.date!) || a.title.localeCompare(b.title));
    const since = shown.filter((it) => it.type === 'since' && it.date).sort((a, b) => b.date!.localeCompare(a.date!) || a.title.localeCompare(b.title));
    const ongoing = shown.filter((it) => it.type === 'ongoing' || !it.date);
    const days = (d: string) => Math.round((toMs(d) - toMs(data.today)) / DAY);
    const buckets = BUCKETS.map((b) => ({ ...b, items: deadlines.filter((it) => b.test(days(it.date!))) })).filter((b) => b.items.length);
    const ongoingByKind = new Map<string, Item[]>();
    for (const it of ongoing) ongoingByKind.set(it.kind, [...(ongoingByKind.get(it.kind) ?? []), it]);
    return { counts, buckets, since, ongoingByKind, total: shown.length };
  }, [data, hidden, urgentOnly]);

  if (error) return <div className="px-5 py-4 text-sm text-red-600">{error}</div>;
  if (!data || !view) return <div className="space-y-2 px-5 py-4"><Skeleton className="h-4 w-1/3" /><Skeleton className="h-4 w-2/3" /><Skeleton className="h-4 w-1/2" /></div>;
  if (data.items.length === 0) return <EmptyState compact title="All clear" description="Nothing coming due and nothing broken." />;

  return (
    <div>
      {/* Filters: which kinds of item to show (remembered per browser). */}
      <div className="flex flex-wrap items-center gap-1.5 border-b border-slate-200 px-5 py-3">
        {[...view.counts].map(([k, n]) => (
          <button key={k} type="button" onClick={() => toggleKind(k)}
            className={`rounded-full border px-2 py-0.5 text-xs ${hidden.has(k) ? 'border-slate-200 text-slate-400 line-through' : 'border-slate-300 bg-slate-50 text-slate-700'}`}
            aria-pressed={!hidden.has(k)}>
            {data.labels[k] ?? k} <span className="tabular-nums text-slate-400">{n}</span>
          </button>
        ))}
        <label className="ml-auto flex items-center gap-1.5 text-xs text-slate-600">
          <input type="checkbox" checked={urgentOnly} onChange={(e) => setUrgentOnly(e.target.checked)} />Urgent only
        </label>
      </div>

      {view.total === 0 ? (
        <EmptyState compact title="Nothing matches" description="Every kind is filtered out — click a chip to show it again." />
      ) : (
        <div className="grid grid-cols-1 gap-px bg-slate-100 lg:grid-cols-2">
          <section className="bg-surface px-5 py-4">
            <h3 className="mb-3 flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider text-slate-500"><CalendarClock className="h-3.5 w-3.5" />Coming due</h3>
            {view.buckets.length === 0 ? <p className="text-sm text-slate-500">No deadlines.</p> : (
              <div className="space-y-4">
                {view.buckets.map((b) => (
                  <div key={b.key}>
                    <div className={`mb-2 text-sm font-semibold ${b.tone}`}>{b.label} <span className="font-normal text-slate-400">({b.items.length})</span></div>
                    <ByDay items={b.items} today={data.today} onChanged={reload} />
                  </div>
                ))}
              </div>
            )}
          </section>
          <section className="bg-surface px-5 py-4">
            <h3 className="mb-3 flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider text-slate-500"><AlertTriangle className="h-3.5 w-3.5" />Open problems, newest first</h3>
            {view.since.length === 0 ? <p className="text-sm text-slate-500">None.</p> : <ByDay items={view.since} today={data.today} onChanged={reload} />}
            {view.ongoingByKind.size > 0 && (
              <>
                <h3 className="mb-2 mt-6 flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider text-slate-500"><InfinityIcon className="h-3.5 w-3.5" />Ongoing (no date)</h3>
                <div className="space-y-3">
                  {[...view.ongoingByKind].map(([k, list]) => (
                    <div key={k}>
                      <div className="mb-0.5 flex items-center gap-1.5 text-xs font-medium text-slate-600"><CircleDot className="h-3 w-3 text-slate-400" />{data.labels[k] ?? k} <span className="font-normal text-slate-400">({list.length})</span></div>
                      <ul>{list.slice(0, 20).map((it) => <Row key={it.key} it={it} today={data.today} onChanged={reload} />)}</ul>
                      {list.length > 20 && <p className="pl-4 text-xs text-slate-400">…and {list.length - 20} more</p>}
                    </div>
                  ))}
                </div>
              </>
            )}
          </section>
        </div>
      )}
      {!!data.acknowledged && <p className="border-t border-slate-200 px-5 py-2 text-xs text-slate-400">{data.acknowledged} acknowledged item{data.acknowledged === 1 ? '' : 's'} hidden — see them under each section in “By type”.</p>}
      {data.truncated && <p className="border-t border-slate-200 px-5 py-2 text-xs text-slate-400">Showing the first 300 of some kinds — use the domain filters for the full lists.</p>}
    </div>
  );
}
